// src/components/shared/forms/CitySelect.tsx
//
// Single "City" picker that searches cities globally (offline data from
// country-state-city) and resolves the owning country + state behind the scenes.
// The UI shows only City, but the emitted value carries countryCode/country/state
// as well, so the backend still receives all three.

import React from "react";
import AsyncSelect from "react-select/async";
import type { SingleValue } from "react-select";
import Field from "@/components/shared/ui/Field";
import {
  City as CSCity,
  Country as CSCountry,
  State as CSState,
  useLocationCatalog,
} from "@/lib/locationCatalog";

export type CityValue = {
  countryCode?: string; // ISO2
  country?: string;
  state?: string; // region name
  city?: string;
};

type CityOption = {
  value: string;
  label: string;
  data: Required<CityValue>;
};

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "0.5rem",
    borderColor: state.isFocused ? "#0ea5e9" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(14,165,233,0.2)" : "none",
    ":hover": { borderColor: "#0ea5e9" },
    fontSize: "0.875rem",
  }),
  valueContainer: (b: any) => ({ ...b, padding: "0 12px" }),
  placeholder: (b: any) => ({ ...b, color: "#9ca3af" }),
  singleValue: (b: any) => ({ ...b, color: "#111827" }),
  menu: (b: any) => ({ ...b, zIndex: 70, borderRadius: "0.5rem" }),
  option: (b: any, s: any) => ({
    ...b,
    fontSize: "0.875rem",
    backgroundColor: s.isFocused ? "#f3f4f6" : "white",
    color: "#111827",
    cursor: "pointer",
  }),
};

export default function CitySelect({
  value,
  onChange,
  instancePrefix = "city",
  minChars = 2,
  maxResults = 50,
  label = "City",
}: {
  value: CityValue;
  onChange: (v: CityValue) => void;
  instancePrefix?: string;
  minChars?: number;
  maxResults?: number;
  label?: string;
}) {
  // Cities come from the Locations module (Settings → Locations). The hook
  // re-renders this component once the catalogue has loaded, and the memo keys
  // on that so the list is never snapshotted while still empty.
  const catalog = useLocationCatalog();
  const allCities = React.useMemo(
    () => CSCity.getAllCities(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getAllCities reads
    // a module-level cache, so readiness is the real dependency here.
    [catalog.isReady]
  );

  const loadOptions = async (input: string): Promise<CityOption[]> => {
    const q = input.trim().toLowerCase();
    if (q.length < minChars) return [];

    const out: CityOption[] = [];
    for (let i = 0; i < allCities.length; i++) {
      const c = allCities[i];
      if (!c.name.toLowerCase().includes(q)) continue;

      const country = CSCountry.getCountryByCode(c.countryCode);
      const state = c.stateCode
        ? CSState.getStateByCodeAndCountry(c.stateCode, c.countryCode)
        : undefined;

      out.push({
        value: `${c.name}|${c.stateCode}|${c.countryCode}`,
        label: [c.name, state?.name, country?.name].filter(Boolean).join(", "),
        data: {
          city: c.name,
          countryCode: c.countryCode,
          country: country?.name ?? "",
          state: state?.name ?? "",
        },
      });

      if (out.length >= maxResults) break;
    }

    return out;
  };

  const selectedOption: CityOption | null = value?.city
    ? {
        value: "__current__",
        label: [value.city, value.state, value.country].filter(Boolean).join(", "),
        data: {
          city: value.city ?? "",
          countryCode: value.countryCode ?? "",
          country: value.country ?? "",
          state: value.state ?? "",
        },
      }
    : null;

  return (
    <Field label={label}>
      <AsyncSelect
        instanceId={`${instancePrefix}-city`}
        classNamePrefix="rs"
        styles={selectStyles}
        cacheOptions
        isClearable
        loadOptions={loadOptions}
        value={selectedOption}
        onChange={(opt: SingleValue<CityOption>) =>
          onChange(
            opt
              ? opt.data
              : { countryCode: "", country: "", state: "", city: "" }
          )
        }
        loadingMessage={() => "Searching cities..."}
        noOptionsMessage={({ inputValue }) =>
          (inputValue?.length ?? 0) < minChars
            ? `Type at least ${minChars} letters`
            : "No matches"
        }
        placeholder="Search city..."
      />
    </Field>
  );
}
